import {FC, useState} from 'react'
import {api, Coupon, CouponInput} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'
import {toast} from '../../utils/toast'

// ── Toggle Switch ─────────────────────────────────────────────────────────────
const Toggle: FC<{checked: boolean; onChange: () => void}> = ({checked, onChange}) => (
  <div onClick={onChange} style={{
    width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
    background: checked ? '#22c55e' : '#e2e8f0',
    position: 'relative', transition: 'background 0.2s',
  }}>
    <div style={{
      position: 'absolute', top: 3, left: checked ? 21 : 3,
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
    }} />
  </div>
)

// "$50" for fixed, "10%" for percent — with the cap appended when set.
const discountText = (c: Coupon): string =>
  c.discountType === 'percent'
    ? `${c.discountValue}%${c.maxDiscount ? ` (max $${c.maxDiscount})` : ''}`
    : `$${c.discountValue}`

// ISO timestamp → YYYY-MM-DD for an <input type="date">; '' when unset.
const toDateInput = (iso: string | null): string => (iso ? iso.slice(0, 10) : '')

const fmtDate = (iso: string | null): string => {
  if (!iso) return 'No expiry'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? 'No expiry' : d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const CouponsPage: FC = () => {
  const {items: coupons, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated<Coupon>((p, l, s) => api.getCouponsPaged(p, l, s), {limit: 20})

  const [actionError, setActionError]   = useState('')
  const [showModal, setShowModal]       = useState(false)
  const [editTarget, setEditTarget]     = useState<Coupon | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Coupon | null>(null)
  const [saving, setSaving]             = useState(false)

  // Form state
  const [fCode, setFCode]             = useState('')
  const [fLabel, setFLabel]           = useState('')
  const [fDescription, setFDescription] = useState('')
  const [fType, setFType]             = useState<'fixed' | 'percent'>('fixed')
  const [fValue, setFValue]           = useState('')
  const [fMaxDiscount, setFMaxDiscount] = useState('')
  const [fMinSubtotal, setFMinSubtotal] = useState('')
  const [fExpiresAt, setFExpiresAt]   = useState('')
  const [fUsageLimit, setFUsageLimit] = useState('')
  const [fActive, setFActive]         = useState(true)
  const [formError, setFormError]     = useState('')

  const openAdd = () => {
    setEditTarget(null)
    setFCode(''); setFLabel(''); setFDescription('')
    setFType('fixed'); setFValue(''); setFMaxDiscount(''); setFMinSubtotal('')
    setFExpiresAt(''); setFUsageLimit(''); setFActive(true); setFormError('')
    setShowModal(true)
  }

  const openEdit = (c: Coupon) => {
    setEditTarget(c)
    setFCode(c.code); setFLabel(c.label); setFDescription(c.description)
    setFType(c.discountType); setFValue(String(c.discountValue))
    setFMaxDiscount(c.maxDiscount != null ? String(c.maxDiscount) : '')
    setFMinSubtotal(c.minSubtotal ? String(c.minSubtotal) : '')
    setFExpiresAt(toDateInput(c.expiresAt))
    setFUsageLimit(c.usageLimit != null ? String(c.usageLimit) : '')
    setFActive(c.active); setFormError('')
    setShowModal(true)
  }

  const handleSave = async () => {
    const code = fCode.trim().toUpperCase()
    const value = parseFloat(fValue)
    if (!code) { setFormError('Code is required.'); return }
    if (!isFinite(value) || value <= 0) { setFormError('Discount value must be greater than 0.'); return }
    if (fType === 'percent' && value > 100) { setFormError('A percentage discount cannot exceed 100%.'); return }

    // Build the payload — blank optional fields go as null so an edit can clear them.
    const payload: CouponInput = {
      code,
      label: fLabel.trim(),
      description: fDescription.trim(),
      discountType: fType,
      discountValue: value,
      maxDiscount: fType === 'percent' && fMaxDiscount.trim() ? parseFloat(fMaxDiscount) : null,
      minSubtotal: fMinSubtotal.trim() ? parseFloat(fMinSubtotal) : 0,
      expiresAt: fExpiresAt ? fExpiresAt : null,
      usageLimit: fUsageLimit.trim() ? parseInt(fUsageLimit, 10) : null,
      active: fActive,
    }

    setSaving(true)
    try {
      if (editTarget) {
        await api.updateCoupon(editTarget.id, payload)
      } else {
        await api.createCoupon(payload)
      }
      reload()
      setShowModal(false)
      toast(editTarget ? 'Coupon updated' : 'Coupon created')
    } catch (e: any) {
      setFormError(e.message ?? 'Failed to save.')
      toast(e?.message ?? 'Save failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await api.deleteCoupon(deleteTarget.id)
      reload()
      setDeleteTarget(null)
      toast('Coupon deleted')
    } catch (e: any) {
      setActionError(e.message)
      toast(e?.message ?? 'Delete failed', 'error')
    }
  }

  const toggleStatus = async (c: Coupon) => {
    try {
      await api.updateCoupon(c.id, {active: !c.active})
      reload()
      toast(c.active ? 'Coupon deactivated' : 'Coupon activated')
    } catch (e: any) {
      setActionError(e.message)
      toast(e?.message ?? 'Update failed', 'error')
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc',
    outline: 'none', boxSizing: 'border-box' as const,
  }

  const labelStyle = {
    fontSize: 13, fontWeight: 600 as const, color: '#374151',
    display: 'block' as const, marginBottom: 6,
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Coupons</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Manage promo codes used at ticket checkout in the mobile app</p>
      </div>

      {/* Toolbar */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, alignItems: 'center',
      }}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search by code or label...' value={search}
            onChange={e => setSearch(e.target.value)}
            style={{...inputStyle, paddingLeft: 38, background: '#fff'}} />
        </div>
        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
        <button onClick={openAdd}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: '#3b82f6', cursor: 'pointer'}}>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'><line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/></svg>
          Add Coupon
        </button>
      </div>

      {(error || actionError) && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error || actionError}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['CODE', 'DISCOUNT', 'MIN SPEND', 'EXPIRES', 'USAGE', 'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coupons.map((item, i) => (
                  <tr key={item.id}
                    style={{borderBottom: i < coupons.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                  >
                    {/* Code + label */}
                    <td style={{padding: '14px 20px', minWidth: 180}}>
                      <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        <span style={{fontWeight: 700, color: '#0f172a', fontSize: 14, fontFamily: 'monospace'}}>{item.code}</span>
                        {item.label ? <span style={{fontSize: 12, color: '#64748b'}}>{item.label}</span> : null}
                      </div>
                    </td>

                    {/* Discount */}
                    <td style={{padding: '14px 20px'}}>
                      <span style={{fontSize: 13, fontWeight: 600, color: '#7c3aed', background: '#f5f3ff', padding: '3px 10px', borderRadius: 20}}>
                        {discountText(item)}
                      </span>
                    </td>

                    {/* Min spend */}
                    <td style={{padding: '14px 20px', color: '#64748b', fontSize: 13}}>
                      {item.minSubtotal > 0 ? `$${item.minSubtotal}` : '—'}
                    </td>

                    {/* Expires */}
                    <td style={{padding: '14px 20px', color: '#64748b', fontSize: 13, whiteSpace: 'nowrap'}}>
                      {fmtDate(item.expiresAt)}
                    </td>

                    {/* Usage */}
                    <td style={{padding: '14px 20px', color: '#64748b', fontSize: 13}}>
                      {item.usedCount}{item.usageLimit != null ? ` / ${item.usageLimit}` : ' / ∞'}
                    </td>

                    {/* Status */}
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <Toggle checked={item.active} onChange={() => toggleStatus(item)} />
                        <span style={{fontSize: 13, fontWeight: 500, color: item.active ? '#16a34a' : '#94a3b8'}}>
                          {item.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', gap: 8}}>
                        <button onClick={() => openEdit(item)}
                          style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc' }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff' }}>
                          Edit
                        </button>
                        <button onClick={() => setDeleteTarget(item)}
                          style={{padding: '6px 14px', border: '1px solid #fecaca', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#ef4444', background: '#fff', cursor: 'pointer'}}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#fef2f2' }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff' }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {coupons.length === 0 && (
                  <tr><td colSpan={7} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No coupons found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {coupons.length} of {total} coupons</span>
          </div>
        )}
      </div>

      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />

      {/* Add / Edit Modal */}
      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.15)'}}>
            {/* Header */}
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0}}>
                {editTarget ? 'Edit Coupon' : 'Add New Coupon'}
              </h3>
              <button onClick={() => setShowModal(false)}
                style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
              </button>
            </div>

            {/* Body */}
            <div style={{padding: '24px', display: 'flex', flexDirection: 'column', gap: 18}}>
              {formError && (
                <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13}}>
                  {formError}
                </div>
              )}

              <div style={{display: 'flex', gap: 14}}>
                <div style={{flex: 1}}>
                  <label style={labelStyle}>Code <span style={{color: '#ef4444'}}>*</span></label>
                  <input placeholder='e.g. SUMMER25' value={fCode}
                    onChange={e => setFCode(e.target.value.toUpperCase())}
                    style={{...inputStyle, fontFamily: 'monospace'}} />
                </div>
                <div style={{flex: 1}}>
                  <label style={labelStyle}>Label</label>
                  <input placeholder='e.g. 25% OFF' value={fLabel} onChange={e => setFLabel(e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Description</label>
                <input placeholder='e.g. Summer sale discount' value={fDescription} onChange={e => setFDescription(e.target.value)} style={inputStyle} />
              </div>

              <div style={{display: 'flex', gap: 14}}>
                <div style={{flex: 1}}>
                  <label style={labelStyle}>Type <span style={{color: '#ef4444'}}>*</span></label>
                  <select value={fType} onChange={e => setFType(e.target.value as 'fixed' | 'percent')} style={inputStyle}>
                    <option value='fixed'>Fixed ($ off)</option>
                    <option value='percent'>Percentage (% off)</option>
                  </select>
                </div>
                <div style={{flex: 1}}>
                  <label style={labelStyle}>
                    {fType === 'percent' ? 'Percentage' : 'Amount ($)'} <span style={{color: '#ef4444'}}>*</span>
                  </label>
                  <input type='number' placeholder={fType === 'percent' ? 'e.g. 25' : 'e.g. 50'} value={fValue}
                    onChange={e => setFValue(e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div style={{display: 'flex', gap: 14}}>
                {fType === 'percent' && (
                  <div style={{flex: 1}}>
                    <label style={labelStyle}>Max Discount ($)</label>
                    <input type='number' placeholder='Optional cap' value={fMaxDiscount}
                      onChange={e => setFMaxDiscount(e.target.value)} style={inputStyle} />
                  </div>
                )}
                <div style={{flex: 1}}>
                  <label style={labelStyle}>Min Spend ($)</label>
                  <input type='number' placeholder='e.g. 100 (optional)' value={fMinSubtotal}
                    onChange={e => setFMinSubtotal(e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div style={{display: 'flex', gap: 14}}>
                <div style={{flex: 1}}>
                  <label style={labelStyle}>Expires</label>
                  <input type='date' value={fExpiresAt} onChange={e => setFExpiresAt(e.target.value)} style={inputStyle} />
                </div>
                <div style={{flex: 1}}>
                  <label style={labelStyle}>Usage Limit</label>
                  <input type='number' placeholder='Blank = unlimited' value={fUsageLimit}
                    onChange={e => setFUsageLimit(e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                  <Toggle checked={fActive} onChange={() => setFActive(a => !a)} />
                  <span style={{fontSize: 14, fontWeight: 500, color: fActive ? '#16a34a' : '#94a3b8'}}>
                    {fActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, position: 'sticky', bottom: 0, background: '#fff'}}>
              <button onClick={() => setShowModal(false)} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>
                {saving ? 'Saving...' : editTarget ? 'Save Changes' : 'Add Coupon'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div onClick={() => setDeleteTarget(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 420, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', padding: '32px 28px', textAlign: 'center'}}>
            <div style={{width: 56, height: 56, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'}}>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#ef4444' strokeWidth='2'>
                <polyline points='3 6 5 6 21 6'/><path d='M19 6l-1 14H6L5 6'/>
                <path d='M10 11v6M14 11v6'/><path d='M9 6V4h6v2'/>
              </svg>
            </div>
            <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 8px'}}>Delete Coupon</h3>
            <p style={{fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6}}>
              Are you sure you want to delete <strong style={{color: '#0f172a'}}>{deleteTarget.code}</strong>?
              It will no longer be redeemable at checkout.
            </p>
            <div style={{display: 'flex', gap: 10, justifyContent: 'center'}}>
              <button onClick={() => setDeleteTarget(null)}
                style={{padding: '9px 24px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleDelete}
                style={{padding: '9px 24px', borderRadius: 8, border: 'none', background: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff'}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const CouponsPageWrapper: FC = () => <CouponsPage />

export {CouponsPageWrapper}
